import { ServicePriceType } from './enums';
import { formatPriceMixin } from './currencyMixin';
import { ZeroPriceTextEnum, ZeroPriceTextMixin } from './enums/ZeroPriceText';
import PriceMethods from './PriceMethods';
import { useDate } from '@/composables/useDate.js';

const { parseDate, add, parse, subtract, isoWeekday, startOf, daysInMonth } = useDate();

export const fullDate = {
    methods: {
        fullDate(date) {
            const dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
            var d = new Date(date).toLocaleDateString(getLocaleString(), dateOptions);
            d = d.charAt(0).toUpperCase() + d.slice(1);
            d = d.replace('den', 'd.');
            return d;
        },
    },
};

export const fullUrl = {
    methods: {
        fullUrl(url = '') {
            return window.location.origin + url;
        },
    },
};

export const shortDateWithTime = {
    methods: {
        superShortDateWithDayOfWeek(date) {
            let thisDate = new Date(date);
            let m = thisDate.toLocaleDateString(getLocaleString(), { month: 'short' });
            let d = thisDate.getDate();
            let j = thisDate.getDay();
            let shortDanishDates = ['søn', 'man', 'tir', 'ons', 'tor', 'fre', 'lør'];
            return shortDanishDates[j] + '. ' + d + '. ' + m;
        },
        shortDate(date) {
            let thisDate = new Date(date);
            let m = thisDate.toLocaleDateString(getLocaleString(), { month: 'short' });
            let d = thisDate.getDate();
            return d + '. ' + m;
        },
        shortDateWithTime(date) {
            let thisDate = new Date(date);
            let y = thisDate.getFullYear();
            let m = thisDate.toLocaleDateString(getLocaleString(), { month: 'short' });
            let d = thisDate.getDate();
            let h = thisDate.getHours();
            let i = thisDate.getMinutes();
            return d + '. ' + m + ' ' + y + ', ' + (h < 10 ? '0' : '') + h + ':' + (i < 10 ? '0' : '') + i;
        },
        superShortDateWithTime(date) {
            let thisDate = new Date(date);
            let y = thisDate.getFullYear();
            let m = thisDate.toLocaleDateString(getLocaleString(), { month: 'short' });
            let d = thisDate.getDate();
            let h = thisDate.getHours();
            let i = thisDate.getMinutes();
            return d + '. ' + m + ' ' + y + ', ' + (h < 10 ? '0' : '') + h + ':' + (i < 10 ? '0' : '') + i;
        },
        dmy(date) {
            let thisDate = new Date(date);
            let y = thisDate.getFullYear().toString().substring(2);
            let m = thisDate.getMonth() + 1;
            let d = thisDate.getDate();
            return (d < 10 ? '0' : '') + d + '/' + (m < 10 ? '0' : '') + m + '/' + y;
        },
        dmyWithTime(date) {
            let thisDate = new Date(date);
            let y = thisDate.getFullYear().toString().substring(2);
            let m = thisDate.getMonth() + 1;
            let d = thisDate.getDate();
            let h = thisDate.getHours();
            let i = thisDate.getMinutes();
            return (
                (d < 10 ? '0' : '') +
                d +
                '/' +
                (m < 10 ? '0' : '') +
                m +
                '/' +
                y +
                ', ' +
                (h < 10 ? '0' : '') +
                h +
                ':' +
                (i < 10 ? '0' : '') +
                i
            );
        },
        ultraShortDateWithTime(date) {
            let thisDate = new Date(date);
            let m = thisDate.getMonth() + 1;
            let d = thisDate.getDate();
            let h = thisDate.getHours();
            let i = thisDate.getMinutes();
            return (d < 10 ? '0' : '') + d + '/' + m + ' ' + (h < 10 ? '0' : '') + h + ':' + (i < 10 ? '0' : '') + i;
        },
    },
};

export const prettyPrice = {
    mixins: [ZeroPriceTextMixin, formatPriceMixin, PriceMethods],
    methods: {
        prettyPrice(service, currency, total = false, divide = false, vat = false, zeroAllowed = false) {
            // Set VAT
            let servicePrice = vat ? service.modified_price_vat : service.modified_price;
            let bundlePrice = vat ? service.bundle_price_vat : service.bundle_price;

            // Divide
            let modifiedPrice = [divide ? this.parseFromCents(servicePrice) : servicePrice];
            let modifiedBundlePrice = [divide ? this.parseFromCents(bundlePrice) : bundlePrice];

            // Get difference
            let difference = modifiedPrice - modifiedBundlePrice;

            if (parseFloat(modifiedBundlePrice) === 0 && parseFloat(modifiedPrice) !== 0) {
                if (!total) {
                    if (ServicePriceType.isPerPersonBased(service)) {
                        return '+' + this.formatPrice(modifiedPrice, currency) + this.$t('/person');
                    } else {
                        return '+' + this.formatPrice(modifiedPrice, currency) + this.$t('/unit');
                    }
                } else {
                    return (
                        '+' +
                        [
                            total === true
                                ? this.formatPrice(modifiedPrice * service.quantity, currency)
                                : this.formatPrice(modifiedPrice, currency),
                        ]
                    );
                }
            } else if (difference === 0) {
                if (zeroAllowed) {
                    if (ServicePriceType.isPerPersonBased(service)) {
                        return this.formatPrice(modifiedPrice, currency) + this.$t('/person');
                    } else {
                        return this.formatPrice(modifiedPrice, currency) + this.$t('/unit');
                    }
                } else {
                    return service.zero_price_text
                        ? this.zeroPriceTextDisplayName(service.zero_price_text)
                        : this.zeroPriceTextDisplayName(ZeroPriceTextEnum.INCLUDED_IN_PACKAGE);
                }
            } else {
                if (!total) {
                    if (ServicePriceType.isPerPersonBased(service)) {
                        return (
                            (difference > 0 ? '+' : '') + this.formatPrice(difference, currency) + this.$t('/person')
                        );
                    } else {
                        return (difference > 0 ? '+' : '') + this.formatPrice(difference, currency) + this.$t('/unit');
                    }
                } else {
                    return (
                        (difference > 0 ? '+' : '') +
                        [
                            total === true
                                ? this.formatPrice(difference * service.quantity, currency)
                                : this.formatPrice(difference, currency),
                        ]
                    );
                }
            }
        },
    },
};

export const identifier = {
    methods: {
        identifier(se = null, ca = null, sc = null, pr = null) {
            let identifier = '';
            identifier += se !== null ? 'se' + se : '';
            identifier += ca !== null ? 'ca' + ca : '';
            identifier += sc !== null ? 'sc' + sc : '';
            identifier += pr !== null ? 'pr' + pr : '';
            return identifier;
        },
    },
};

function localAndPrettyDateFunction(date, type = '') {
    const dateOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    const timeOptions = { hour: '2-digit', minute: '2-digit' };
    if (date !== null) {
        let localDate = new Date(date).toLocaleDateString(getLocaleString(), dateOptions);
        let localTime = new Date(date).toLocaleTimeString(getLocaleString(), timeOptions);
        localTime.replace('.', ':');
        if (type === '') {
            return localDate + ', ' + localTime;
        } else if (type === 'time-only') {
            return localTime;
        } else if (type === 'date-only') {
            return localDate;
        }
    } else {
        return null;
    }
}

export const localAndPrettyDate = {
    methods: {
        localAndPrettyDate(date, type = '') {
            return localAndPrettyDateFunction(date, type);
        },
    },
};

export const dateDMY = {
    methods: {
        dateDMY(date) {
            return parseDate(date, 'dd/MM/yyyy');
        },
    },
};

export const prettyDurationInfo = {
    computed: {
        prettyDurationInfo() {
            let accommodationEnabled = false;
            let search = this.$store.getters.getSearch;
            let eventType = this.$store.getters.getEventType;
            if (search.startTime !== null && search.endTime !== null && search.participants !== null) {
                let info =
                    this.$t('Your selection') +
                    ': ' +
                    localAndPrettyDateFunction(search.startTime) +
                    ' - ' +
                    localAndPrettyDateFunction(search.endTime) +
                    ', ' +
                    search.participants +
                    ' ' +
                    this.$t('delegates');
                if (eventType.accommodation_active && accommodationEnabled) {
                    info +=
                        ', ' +
                        (search.accommodation ? this.$t('with accommodation') : this.$t('without accommodation'));
                }
                return info;
            } else {
                return this.$t('You have not selected a date and duration yet');
            }
        },
    },
};

export function priceCalculator(sections, participants, divided = false) {
    var prices = {
        total: 0,
        totalVat: 0,
        bundle: 0,
        bundleVat: 0,
        addOns: 0,
        addOnsVat: 0,
        perPerson: 0,
        perPersonVat: 0,
        bundlePerPerson: 0,
        bundlePerPersonVat: 0,
    };
    sections.forEach((section) => {
        if (section.categories !== undefined) {
            section.categories.forEach((category) => {
                if (category.services !== undefined) {
                    category.services.forEach((service) => {
                        if (
                            service.modified_price !== null &&
                            service.quantity !== null &&
                            service.modified_price !== undefined &&
                            service.quantity !== undefined &&
                            service.quantity > 0
                        ) {
                            prices.total += service.modified_price * service.quantity;
                            prices.totalVat += service.modified_price_vat * service.quantity;
                            prices.bundle += service.bundle_price * service.quantity;
                            prices.bundleVat += service.bundle_price_vat * service.quantity;
                        }
                    });
                }
                if (category.subCategories !== undefined && category.subCategories.length > 0) {
                    category.subCategories.forEach((subCategory) => {
                        if (subCategory.services !== undefined) {
                            subCategory.services.forEach((service) => {
                                if (
                                    service.modified_price !== null &&
                                    service.quantity !== null &&
                                    service.modified_price !== undefined &&
                                    service.quantity !== undefined &&
                                    service.quantity > 0
                                ) {
                                    prices.total += service.modified_price * service.quantity;
                                    prices.totalVat += service.modified_price_vat * service.quantity;
                                    prices.bundle += service.bundle_price * service.quantity;
                                    prices.bundleVat += service.bundle_price_vat * service.quantity;
                                }
                            });
                        }
                    });
                }
            });
        }
    });

    prices.addOns = prices.total - prices.bundle;
    prices.addOnsVat = prices.totalVat - prices.bundleVat;
    prices.perPerson = prices.total / participants;
    prices.perPersonVat = prices.totalVat / participants;
    prices.bundlePerPerson = prices.bundle / participants;
    prices.bundlePerPersonVat = prices.bundleVat / participants;

    if (divided) {
        const centsDivider = PriceMethods.methods.getCentsDivider();
        prices.total = prices.total / centsDivider;
        prices.totalVat = prices.totalVat / centsDivider;
        prices.bundle = prices.bundle / centsDivider;
        prices.bundleVat = prices.bundleVat / centsDivider;
        prices.addOns = prices.addOns / centsDivider;
        prices.addOnsVat = prices.addOnsVat / centsDivider;
        prices.perPerson = prices.perPerson / centsDivider;
        prices.perPersonVat = prices.perPersonVat / centsDivider;
        prices.bundlePerPerson = prices.bundlePerPerson / centsDivider;
        prices.bundlePerPersonVat = prices.bundlePerPersonVat / centsDivider;
    }
    return prices;
}

export const paddingClassesContainers = {
    data() {
        return {
            paddingClassesContainers: 'px-4 md:px-8 xl:px-10 2xl:px-4',
            paddingClassesContainersWithoutMobile: 'md:px-8 xl:px-10 2xl:px-4',
        };
    },
};

export function quantityCleaner(quantity, min_value = null, max_value = null) {
    let modifiedQuantity = parseInt(quantity);
    if (isNaN(modifiedQuantity)) {
        modifiedQuantity = min_value === null ? 0 : min_value;
    }
    if (min_value !== null && modifiedQuantity < min_value) {
        modifiedQuantity = min_value;
    }
    if (max_value !== null && modifiedQuantity > max_value) {
        modifiedQuantity = max_value;
    }
    return modifiedQuantity;
}

export function getLocaleString() {
    let defaultLanguage = 'en';
    var searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('language')) {
        defaultLanguage = searchParams.get('language');
    } else if (window.location.pathname.includes('/en')) {
        defaultLanguage = 'en';
    } else if (window.location.pathname.includes('/')) {
        defaultLanguage = 'da';
    }
    return defaultLanguage;
}

export const createTimesMixin = {
    methods: {
        createTimes(min, max, timesPerHour = 1) {
            let times = [];
            let modifiedMax = max;
            if (max > 24) {
                modifiedMax = 24;
            }
            for (let h = min; h <= modifiedMax; h++) {
                for (let i = 0; i < 60; i += 60 / timesPerHour) {
                    if (h === max && i !== 0) {
                        continue;
                    }

                    // Super hacky 23:59 solution
                    if (h === 24) {
                        h = 23;
                        i = 59;
                    }

                    times.push({
                        displayName: (h < 10 ? '0' : '') + h + ':' + (i < 10 ? '0' : '') + i,
                        h: h,
                        i: i,
                        searchValues: [
                            (h < 10 ? '0' : '') + h + ':' + (i < 10 ? '0' : '') + i,
                            (h < 10 ? '0' : '') + h + '' + (i < 10 ? '0' : '') + i,
                            (h < 10 ? '0' : '') + h + '.' + (i < 10 ? '0' : '') + i,
                        ],
                        value: (h < 10 ? '0' : '') + h + ':' + (i < 10 ? '0' : '') + i + ':00',
                    });

                    // Super hacky but stops loop
                    if (h === 23 && i === 59) {
                        h = 24;
                        i = 1;
                    }
                }
            }
            return times;
        },
        createTimeValuesArray(min, max, timesPerHour = 1) {
            let timeValues = [];
            this.createTimes(min, max, timesPerHour).forEach((time) => {
                timeValues.push(time.value);
            });
            return timeValues;
        },
    },
};

export const createDaysMixin = {
    methods: {
        createDays(amount) {
            let options = [];
            for (let i = 0; i < amount; i++) {
                options.push({
                    value: i + 1,
                    displayName: this.$t('Day') + ' ' + (i + 1),
                });
            }
            return options;
        },
    },
};

export const createMonthsMixin = {
    methods: {
        // Create dates for datepicker
        // TODO: Seems like this method is not being used anywhere?
        createMonths(monthsAmount = 36, paddingsAreNull = true) {
            let months = [];
            let today = new Date();
            for (let i = 0; i < monthsAmount; i++) {
                let days = [];
                let dateMoment = startOf(add(today, i, 'months'), 'month');
                let date = new Date(dateMoment.toISOString());
                let y = date.getFullYear();
                let m = date.getMonth();
                let month_as_text = new Date(y, m, 1).toLocaleDateString(getLocaleString(), { month: 'long' });
                let emptyDaysBefore = isoWeekday(startOf(dateMoment, 'month')) - 1;
                let emptyDaysAfter = 7 - isoWeekday(endOf(dateMoment, 'month'));
                let daysInMonthWithPadding = daysInMonth(dateMoment) + emptyDaysBefore + emptyDaysAfter;
                if (daysInMonthWithPadding < 42) {
                    emptyDaysAfter += 7;
                }

                // Add empty days before
                let firstDayInMonth = new Date(y, m, 1);
                for (let d = 0; d < emptyDaysBefore; d++) {
                    if (paddingsAreNull) {
                        days.push(null);
                    } else {
                        days.push(
                            this.cbDateObject(
                                new Date(parse(subtract(firstDayInMonth, emptyDaysBefore - d, 'days')).toISOString())
                            )
                        );
                    }
                }

                // Add all the days
                for (let d = 1; d <= daysInMonth(dateMoment); d++) {
                    days.push(this.cbDateObject(new Date(y, m, d)));
                }

                // Add empty days after for good measure
                let lastDayInMonth = new Date(y, m, daysInMonth(dateMoment));
                for (let d = 1; d <= emptyDaysAfter; d++) {
                    if (paddingsAreNull) {
                        days.push(null);
                    } else {
                        days.push(this.cbDateObject(new Date(parse(add(lastDayInMonth, d, 'days')).toISOString())));
                    }
                }

                months.push({ as_text: month_as_text, y: y, m: m, days: days });
            }
            this.months = months;
        },
    },
};

export const cbDateTimesMixin = {
    methods: {
        cbDateObject(date) {
            return {
                y: date.getFullYear(),
                m: date.getMonth(),
                d: date.getDate(),
                date: date,
                dateYmd: parseDate(date, 'yyyy-MM-dd'),
            };
        },
        cbTimeObject(date) {
            return {
                h: date.getHours(),
                i: date.getMinutes(),
                displayName: ('0' + date.getHours()).slice(-2) + ':' + ('0' + date.getMinutes()).slice(-2),
            };
        },
        cbDateTimeObjectCombine(cbDateObject, cbTimeObject) {
            return {
                y: cbDateObject.y,
                m: cbDateObject.m,
                d: cbDateObject.d,
                h: cbTimeObject.h,
                i: cbTimeObject.i,
            };
        },
        cbDateObjectToUTC(cbDateTimeObject) {
            let offset =
                new Date(Date.UTC(cbDateTimeObject.y, cbDateTimeObject.m, cbDateTimeObject.d)).getTimezoneOffset() / 60;
            return new Date(
                Date.UTC(
                    cbDateTimeObject.y,
                    cbDateTimeObject.m,
                    cbDateTimeObject.d,
                    cbDateTimeObject.h + offset,
                    cbDateTimeObject.i ? cbDateTimeObject.i : 0
                )
            ).toISOString();
        },
        cbDayName(date, locale = 'en-US') {
            return date.toLocaleDateString(locale, { weekday: 'long' });
        },
    },
};

export const cbDateObjectToUTCMixin = {
    methods: {
        cbDateObjectToUTC(cbDateObject) {
            let offset = new Date(Date.UTC(cbDateObject.y, cbDateObject.m, cbDateObject.d)).getTimezoneOffset() / 60;
            return new Date(
                Date.UTC(
                    cbDateObject.y,
                    cbDateObject.m,
                    cbDateObject.d,
                    cbDateObject.h + offset,
                    cbDateObject.i ? cbDateObject.i : 0
                )
            ).toISOString();
        },
    },
};
